import { useCallback } from "react";

import { useRouter } from "next/router";
import { buildCreateReceiptAppliedOffer } from "../../utils/cart/appliedOffer";
import { handleConfirmReceipt } from "../../utils/RecieptAndCurrentOrderHelper";
import { handleCreateReceipt } from "../../utils/CreateReceiptHelper";
import { OrderTypesEnum, PAYMENT_METHOD, PagesEnum } from "../../utils/enums";
import { isVisitor } from "../../utils/help/user/user";
import {
  selectCartItems,
  selectCartTotal,
  selectIsEligibleOffer,
} from "../redux/selectors/cartSelectors";
import { selectRedeemedLoyaltyOffer } from "../redux/selectors/loyaltyOfferSelectors";
import { selectUseLoyaltyService } from "../redux/selectors/loyaltyServiceSelectors";
import { setAnimatedGlobalBannerState } from "../redux/slices/globalComponentsToggleSlice";
import type { RootState } from "../redux/app/store";
import { useSettledLoyaltyQuote } from "./loyalty/useSettledLoyaltyQuote";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";
import {
  selectCurrentDeliveryConfig,
  selectDeliveryFees,
} from "../redux/selectors/deliverySelectors";

export const usePayHook = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const tableNumber = useAppSelector((state) => state.userInfo.tableNumber);
  const onlinePaymentProvider = useAppSelector(
    (state) => state.config.onlinePaymentProvider
  );

  const { franchise } = router.query;

  const cartAndLoyalty = useAppSelector((state) => state.cartAndLoyaltyReducer);
  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const cartItems = useAppSelector(selectCartItems);
  const cartTotal = useAppSelector(selectCartTotal);
  const isEligibleOffer = useAppSelector(selectIsEligibleOffer);
  const redeemedLoyaltyOffer = useAppSelector(selectRedeemedLoyaltyOffer);
  const token = useAppSelector((state) => state.userInfo.token);
  const deliveryAddress = useAppSelector((state) => state.delivery);
  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const referenceName = useAppSelector(
    (state) => state.reference.referenceName
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  const { selfService } = useAppSelector((state) => state.franchiseStatus);

  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingMethod &&
    !!restaurantInfo?.currencyRoundingLevel;

  const scheduledPickup = useAppSelector(
    (state) => state.cartAndLoyaltyReducer.selectedPickupTime
  );

  const currentDeliveryConfig = useAppSelector(selectCurrentDeliveryConfig);
  const deliveryFees = useAppSelector(selectDeliveryFees);
  const settleLoyaltyQuote = useSettledLoyaltyQuote();

  const pay = useCallback(
    async (
      pageURL: string,
      paymentMethod: PAYMENT_METHOD,
      tips?: number,
      savedCard?: any,
      /**
       * Pay with the stored wallet balance first, and/or with DSQ partner
       * points (loyalty checkout only). `settledState` is the state the caller
       * already settled the quote on and decided from (the cart's free-vs-paid
       * check); it is reused while nothing has moved, so the body is built from
       * that same snapshot instead of settling a second time.
       */
      options?: {
        useWallet?: boolean;
        payWithPartnerPoints?: boolean;
        settledState?: RootState;
      }
    ) => {
      const orderType =
        (orderTypeParamState as OrderTypesEnum) || OrderTypesEnum.TAKE_OUT;

      // loyaltyV2 path — same gate the cart-calculate flow uses.
      if (useLoyaltyService) {
        // The body is built from one snapshot taken once the quote covers the
        // cart, not from this callback's render: callers hold `pay` from an
        // older render (the cart footer binds it in a layout effect), and a
        // calculate still in flight means the stored total is the previous
        // cart's.
        const state = await settleLoyaltyQuote(options?.settledState);
        if (!state) {
          dispatch(
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: "Please try again",
              warning: true,
            })
          );

          return;
        }

        const freshOrderType =
          (state.queryParams.orderTypeParam as OrderTypesEnum) ||
          OrderTypesEnum.TAKE_OUT;
        const freshCart = state.cartAndLoyaltyReducer;
        const loyalty = state.cartLoyaltyV2Reducer.loyalty;

        await handleCreateReceipt({
          pageURL,
          paymentMethod,
          tips,
          savedCard,
          dispatch,
          router,
          orderType: freshOrderType,
          currentDeliveryConfig: selectCurrentDeliveryConfig(state),
          deliveryAddress: state.delivery,
          franchise: franchise as string,
          referenceName: state.reference.referenceName,
          restaurantInfo: state.config.restaurantInfo,
          onlinePaymentProvider: state.config.onlinePaymentProvider,
          tableNumber: state.userInfo.tableNumber,
          hasRoundingMethodAndLevel,
          isVisitor: isVisitor(),
          redirectPage: PagesEnum.Home,
          cartItems: selectCartItems(state),
          loyaltyResult: loyalty.result,
          latestCartVersion: state.cartLoyaltyV2Reducer.latestCartVersion,
          loyaltyLines: loyalty.lines,
          appliedOffer: buildCreateReceiptAppliedOffer(
            loyalty.redemption,
            loyalty.lines
          ),
          receiptNote: freshCart.cart.receiptNote,
          scheduledSlotId: freshCart.selectedScheduledOptions?.selectedSlot?.id,
          numberOfGuests:
            freshOrderType === OrderTypesEnum.DINE_IN
              ? freshCart.selectedScheduledOptions?.numberOfGuests
              : undefined,
          scheduledPickup: freshCart.selectedPickupTime,
          selfService: state.franchiseStatus.selfService,
          useWallet: options?.useWallet,
          payWithPartnerPoints: options?.payWithPartnerPoints,
        });

        return;
      }

      await handleConfirmReceipt({
        pageURL,
        paymentMethod,
        tips,
        savedCard,
        dispatch,
        router,
        orderType,
        cartAndLoyalty,
        currentDeliveryConfig,
        deliveryFees,
        deliveryAddress,
        franchise: franchise as string,
        referenceName,
        restaurantInfo,
        onlinePaymentProvider,
        tableNumber,
        hasRoundingMethodAndLevel,
        isVisitor: isVisitor(),
        redirectPage: PagesEnum.Home,
        cartLoyaltyV2: cartLoyaltyV2,
        cartItems,
        cartTotal,
        isEligibleOffer,
        redeemedOfferId: useLoyaltyService
          ? redeemedLoyaltyOffer?.id
          : undefined,
        selfService,
        scheduledPickup,
      });
    },
    [
      dispatch,
      router,
      orderTypeParamState,
      cartAndLoyalty,
      cartLoyaltyV2,
      cartItems,
      cartTotal,
      isEligibleOffer,
      settleLoyaltyQuote,
      useLoyaltyService,
      redeemedLoyaltyOffer?.id,
      currentDeliveryConfig,
      deliveryFees,
      deliveryAddress,
      franchise,
      referenceName,
      restaurantInfo,
      onlinePaymentProvider,
      tableNumber,
      hasRoundingMethodAndLevel,
      token,
      selfService,
      scheduledPickup,
    ]
  );

  return pay;
};
